package ru.notamedia.qah.validators

import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.JsonNull
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.booleanOrNull
import kotlinx.serialization.json.doubleOrNull
import kotlinx.serialization.json.intOrNull
import kotlinx.serialization.json.longOrNull
import ru.notamedia.qah.models.JsonValidationIssue

object JsonValidator {

    fun validate(expected: JsonElement, actual: JsonElement): List<JsonValidationIssue> {
        return validateRecursive(expected, actual, "")
    }

    private fun validateRecursive(
        expected: JsonElement,
        actual: JsonElement,
        path: String
    ): List<JsonValidationIssue> {
        return when (expected) {
            is JsonObject if actual is JsonObject ->
                compareObjects(
                    expected = expected,
                    actual = actual,
                    path = path,
                )

            is JsonArray if actual is JsonArray ->
                compareArrays(
                    expected = expected,
                    actual = actual,
                    path = path,
                )

            is JsonPrimitive if actual is JsonPrimitive ->
                comparePrimitives(
                    expected = expected,
                    actual = actual,
                    path = path,
                )

            else -> listOf(
                JsonValidationIssue.TypeMismatch(
                    path = path,
                    expectedType = expected.locationType(),
                    actualType = actual.locationType(),
                )
            )
        }
    }

    private fun compareObjects(
        expected: JsonObject,
        actual: JsonObject,
        path: String
    ): List<JsonValidationIssue> {
        val issues = mutableListOf<JsonValidationIssue>()

        val expectedKeys = expected.keys
        val actualKeys = actual.keys

        for (key in expectedKeys) {
            val newPath = if (path.isEmpty()) key else "$path.$key"
            val expectedValue = expected[key]
            val actualValue = actual[key]

            if (actualValue == null) {
                issues += JsonValidationIssue.MissingField(newPath)
            } else if (expectedValue != null) {
                issues += validateRecursive(expectedValue, actualValue, newPath)
            }
        }

        for (key in actualKeys) {
            if (key !in expectedKeys) {
                val newPath = if (path.isEmpty()) key else "$path.$key"
                issues += JsonValidationIssue.UnexpectedField(newPath)
            }
        }

        return issues
    }

    private fun compareArrays(
        expected: JsonArray,
        actual: JsonArray,
        path: String
    ): List<JsonValidationIssue> {
        val issues = mutableListOf<JsonValidationIssue>()

        if (expected.isNotEmpty()) {
            val expectedElement = expected.first()
            for ((index, actualElement) in actual.withIndex()) {
                issues += validateRecursive(expectedElement, actualElement, "$path[$index]")
            }
        }

        return issues
    }

    private fun comparePrimitives(
        expected: JsonPrimitive,
        actual: JsonPrimitive,
        path: String
    ): List<JsonValidationIssue> {
        val expectedType = inferType(expected)
        val actualType = inferType(actual)

        return if (expectedType != actualType) {
            listOf(JsonValidationIssue.TypeMismatch(path, expectedType, actualType))
        } else emptyList()
    }

    private fun inferType(primitive: JsonPrimitive): String {

        if (primitive.isString) {
            return "string"
        }

        if (primitive.booleanOrNull != null) {
            return "boolean"
        }

        val content = primitive.content
        val isWholeNumber = content.matches(Regex("-?\\d+"))
        val isFloatingPoint = content.matches(Regex("-?\\d+\\.\\d+([eE][-+]?\\d+)?"))

        return when {
            isFloatingPoint -> "double"

            isWholeNumber -> {
                val asLong = content.toLongOrNull()

                val asInt = content.toIntOrNull()

                when {
                    asInt != null -> "Int"

                    asLong != null -> "Long"

                    else -> "big_int" // за пределами Long
                }
            }

            else -> "unknown_type"
        }
    }

    private fun JsonElement.locationType(): String {
        return when (this) {
            is JsonArray -> "Массив"
            is JsonObject -> "Объект"
            JsonNull -> "null"
            else -> "Примитив"
        }
    }
}
